import { Coins, Receipt } from "lucide-react"
import * as React from "react"
import { Link } from "react-router-dom"
import { toast } from "sonner"

import { PageHeader } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { useLocale } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import type { AdminExpenseRow } from "@/lib/types"
import { formatDateTime } from "@/lib/utils"

const ALL = "__all__"

const money = (value: number) =>
  value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string | number
}) {
  return (
    <Card>
      <CardContent className="flex items-start gap-3 p-4">
        <div className="bg-muted text-muted-foreground rounded-md p-2">
          <Icon className="size-4" />
        </div>
        <div className="min-w-0">
          <div className="text-muted-foreground text-xs font-medium">{label}</div>
          <div className="text-2xl font-semibold leading-tight">{value}</div>
        </div>
      </CardContent>
    </Card>
  )
}

export function AdminExpensesPage() {
  const { locale } = useLocale()
  const [rows, setRows] = React.useState<AdminExpenseRow[]>([])
  const [loading, setLoading] = React.useState(true)
  const [category, setCategory] = React.useState(ALL)

  React.useEffect(() => {
    setLoading(true)
    api
      .adminExpenses(200, 0)
      .then((page) => setRows(page.items))
      .catch((error) =>
        toast.error(error instanceof ApiError ? error.message : "Could not load expenses."),
      )
      .finally(() => setLoading(false))
  }, [])

  const categories = React.useMemo(
    () => Array.from(new Set(rows.map((r) => r.category))).sort(),
    [rows],
  )
  const visible = category === ALL ? rows : rows.filter((r) => r.category === category)
  const total = visible.reduce((sum, r) => sum + r.amount, 0)

  return (
    <>
      <PageHeader
        title="Expenses"
        description="Every expense tracked by users through the Expense Tracker plugin."
        action={
          <div className="flex items-center gap-2">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button asChild variant="outline">
              <Link to="/admin">Back to admin</Link>
            </Button>
          </div>
        }
      />
      <div className="space-y-6 p-6">
        {loading ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <StatCard icon={Receipt} label="Expenses" value={visible.length.toLocaleString()} />
              <StatCard icon={Coins} label="Total amount" value={money(total)} />
            </div>
            {rows.length >= 200 && (
              <p className="text-muted-foreground text-xs">Showing the 200 most recent expenses.</p>
            )}
            {visible.length === 0 ? (
              <p className="text-muted-foreground text-sm">No expenses recorded.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium">Owner</th>
                      <th className="px-4 py-2 text-right font-medium">Amount</th>
                      <th className="px-4 py-2 text-left font-medium">Category</th>
                      <th className="px-4 py-2 text-left font-medium">Note</th>
                      <th className="px-4 py-2 text-left font-medium">Spent</th>
                      <th className="px-4 py-2 text-left font-medium">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((row) => (
                      <tr key={row.id} className="border-t">
                        <td className="px-4 py-2">{row.owner_email}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{money(row.amount)}</td>
                        <td className="px-4 py-2">{row.category}</td>
                        <td className="text-muted-foreground max-w-xs truncate px-4 py-2">
                          {row.note || "—"}
                        </td>
                        <td className="px-4 py-2 tabular-nums">{row.spent_at}</td>
                        <td className="text-muted-foreground px-4 py-2">
                          {formatDateTime(row.created_at, locale)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
